/**
 * NotificationItem Component (AC-540)
 *
 * Displays a single notification row in the dropdown.
 * Shows notification title, message, type icon, priority styling,
 * and read/unread state.
 *
 * Related Tickets:
 * - AC-532 (Epic: In-App Notification System)
 * - AC-540 (NotificationItem component - THIS TICKET)
 */

import React, { useCallback } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  UserPlusIcon,
  BoltSlashIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { Notification, NotificationType } from '../../types/notification';

// =============================================================================
// Types
// =============================================================================

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
  onDelete: () => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format relative time (e.g., "5 minutes ago").
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Get icon and color based on notification type.
 */
function getIconConfig(type: NotificationType): {
  icon: typeof CheckCircleIcon;
  colorClass: string;
} {
  switch (type) {
    case 'domain_purchased':
    case 'orchestration_completed':
      return { icon: CheckCircleIcon, colorClass: 'text-green-500' };

    case 'domain_purchase_failed':
    case 'orchestration_failed':
    case 'orchestration_timeout':
    case 'scheduled_run_failed':
      return { icon: XCircleIcon, colorClass: 'text-red-500' };

    case 'domain_authorization_required':
      return { icon: ExclamationTriangleIcon, colorClass: 'text-yellow-500' };

    case 'circuit_breaker_tripped':
      return { icon: BoltSlashIcon, colorClass: 'text-yellow-500' };

    case 'credits_exhausted':
      return { icon: CreditCardIcon, colorClass: 'text-red-500' };

    case 'new_follower':
      return { icon: UserPlusIcon, colorClass: 'text-blue-500' };

    default:
      return { icon: CheckCircleIcon, colorClass: 'text-gray-500 dark:text-gray-400' };
  }
}

/**
 * Get priority border class.
 */
function getPriorityClass(priority: string): string {
  switch (priority) {
    case 'urgent':
      return 'border-l-4 border-l-red-500';
    case 'high':
      return 'border-l-4 border-l-orange-500';
    default:
      return '';
  }
}

// =============================================================================
// Component
// =============================================================================

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  onDelete,
}) => {
  const { icon: IconComponent, colorClass } = getIconConfig(notification.notification_type);
  const priorityClass = getPriorityClass(notification.priority);
  const relativeTime = formatRelativeTime(notification.created_at);

  // Handle delete click - stop propagation to prevent onClick
  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete();
    },
    [onDelete]
  );

  // Handle keyboard interaction
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        onClick();
      }
    },
    [onClick]
  );

  // Container classes
  const containerClasses = [
    'relative flex items-start gap-3 p-3',
    'cursor-pointer',
    'hover:bg-gray-100 dark:hover:bg-gray-700',
    'transition-colors',
    notification.is_read ? 'bg-white dark:bg-cap-dark' : 'bg-blue-50 dark:bg-blue-900/20',
    priorityClass,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      data-testid={`notification-item-${notification.notification_id}`}
      className={containerClasses}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="listitem"
      tabIndex={0}
    >
      {/* Unread indicator */}
      {!notification.is_read && (
        <div
          data-testid="unread-indicator"
          className="absolute left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-blue-500"
        />
      )}

      {/* Icon */}
      <div
        data-testid="notification-icon"
        className={`flex-shrink-0 w-6 h-6 mt-0.5 ${colorClass}`}
      >
        <IconComponent className="w-full h-full" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Title */}
        <p className="text-sm font-medium text-gray-900 dark:text-warm-white truncate">
          {notification.title}
        </p>

        {/* Message */}
        {notification.message && (
          <p
            data-testid="notification-message"
            className="mt-0.5 text-sm text-gray-500 dark:text-gray-400 line-clamp-2"
          >
            {notification.message}
          </p>
        )}

        {/* Time */}
        <p
          data-testid="notification-time"
          className="mt-1 text-xs text-gray-400"
        >
          {relativeTime}
        </p>
      </div>

      {/* Delete button */}
      <button
        data-testid={`delete-notification-${notification.notification_id}`}
        className="flex-shrink-0 p-1 rounded hover:bg-gray-200 transition-colors group"
        onClick={handleDeleteClick}
        aria-label={`Delete notification: ${notification.title}`}
      >
        <XMarkIcon className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:text-gray-400" />
      </button>
    </div>
  );
};

export default NotificationItem;
